import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '../../components/Layout.js';
import { api } from '../../lib/api.js';
import { trackPurchase } from '../../lib/analytics.js';

interface OrderResponse {
  id: string;
  status: string;
  currency: string;
  totalCents: number;
  qrs: Array<{ id: string; status: string; ticketSnapshot: { name: string; kind: string } }>;
  buyer: { email: string };
}

export function OrderConfirmationPage() {
  const { orderId, lang } = useParams();
  const { t } = useTranslation('event');

  const { data, isLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => api<OrderResponse>(`/orders/${orderId}`),
    enabled: Boolean(orderId),
    refetchInterval: (q) => (q.state.data?.qrs && q.state.data.qrs.length > 0 ? false : 2000),
  });

  // Fire GA `purchase` exactly once per order (refetchInterval polls until QRs
  // appear, so we guard with a ref to avoid double-counting).
  const purchaseTrackedRef = useRef(false);
  useEffect(() => {
    if (!data || purchaseTrackedRef.current) return;
    if (data.status === 'paid' || data.status === 'fulfilled') {
      purchaseTrackedRef.current = true;
      trackPurchase({
        orderId: data.id,
        eventName: data.qrs[0]?.ticketSnapshot.name ?? 'event',
        currency: data.currency,
        totalCents: data.totalCents,
      });
    }
  }, [data]);

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="max-w-md mx-auto px-4 py-24 text-center text-neutral-400">Loading…</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-24 text-center">
        <h1 className="text-3xl font-bold mb-2">{t('checkout.success_title')}</h1>
        <p
          className="text-neutral-400 mb-8"
          dangerouslySetInnerHTML={{
            __html: t('checkout.success_body', { email: data.buyer.email }),
          }}
        />
        <div className="space-y-3 mb-8">
          {data.qrs.map((q) => (
            <Link
              to={`/${lang}/t/${q.id}`}
              key={q.id}
              className="card flex items-center justify-between"
            >
              <span>{q.ticketSnapshot.name}</span>
              <span className="text-sm text-neutral-400">→</span>
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}
